import { useEffect, useState } from "react";
import { useParams, Link } from "react-router-dom";
import axios from "axios";
import { Scissors, Check, Calendar as CalendarIcon, Timer, ArrowRight } from "lucide-react";
import { format, parseISO } from "date-fns";
import SEOHead, { organizationSchema } from "@/components/SEOHead";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const priorityClasses = {
  high: "border-red-300 text-red-700 bg-red-50",
  medium: "border-amber-300 text-amber-700 bg-amber-50",
  low: "border-blue-300 text-blue-700 bg-blue-50",
};

export default function PublicTaskView() {
  const { token } = useParams();
  const [task, setTask] = useState(null);
  const [error, setError] = useState("");
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    if (!token) {
      setError("Invalid share link");
      setLoading(false);
      return;
    }
    axios
      .get(`${API}/public/tasks/${token}`, { withCredentials: false })
      .then((res) => setTask(res.data))
      .catch((err) =>
        setError(err.response?.status === 404 ? "This shared task is private or no longer exists." : "Couldn't load task")
      )
      .finally(() => setLoading(false));
  }, [token]);

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-white">
        <p className="text-xs uppercase tracking-[0.2em] text-zinc-400">Loading shared task…</p>
      </div>
    );
  }

  if (error || !task) {
    return (
      <div className="min-h-screen bg-white flex flex-col relative">
        <SEOHead title="Shared task unavailable — BreakItDown" path={`/share/${token}`} noindex />
        <Header />
        <main className="flex-1 flex items-center justify-center px-6">
          <div className="text-center max-w-md">
            <p className="text-xs font-semibold uppercase tracking-[0.18em] text-zinc-500 mb-3">
              Link unavailable
            </p>
            <h1 className="text-3xl sm:text-4xl font-semibold tracking-tight leading-none text-zinc-900 mb-4">
              {error || "Not found"}
            </h1>
            <Link to="/" className="text-sm text-[#002FA7] hover:underline">
              Go to BreakItDown →
            </Link>
          </div>
        </main>
        <PublicFooter />
        <PoweredByBadge />
      </div>
    );
  }

  const subtasks = task.subtasks || [];
  const subDone = subtasks.filter((s) => s.completed).length;
  const progressPct = subtasks.length ? Math.round((subDone / subtasks.length) * 100) : 0;

  return (
    <div className="min-h-screen bg-white flex flex-col relative" data-testid="public-task-view">
      <SEOHead
        title={`${task.title} — shared task`}
        description={task.description || `A task shared from BreakItDown — break big jobs into bite-sized subtasks.`}
        path={`/share/${token}`}
        schemas={[organizationSchema()]}
        noindex
      />
      <Header />
      <main className="flex-1 px-6 md:px-10 py-12 max-w-3xl mx-auto w-full">
        <p className="text-xs font-semibold uppercase tracking-[0.18em] text-zinc-500 mb-3">
          Shared {task.shared_by ? `by ${task.shared_by}` : ""} · Read only
        </p>
        <h1 className="text-3xl sm:text-5xl font-semibold tracking-tight leading-tight text-zinc-900 mb-5">
          {task.title}
        </h1>

        <div className="flex flex-wrap items-center gap-2 mb-6">
          {task.priority && (
            <span
              className={`text-[10px] uppercase tracking-[0.12em] font-semibold px-1.5 py-0.5 border ${
                priorityClasses[task.priority] || ""
              }`}
            >
              {task.priority}
            </span>
          )}
          {task.category && (
            <span className="text-[10px] uppercase tracking-[0.12em] font-semibold px-1.5 py-0.5 border border-zinc-300 text-zinc-700 bg-zinc-50">
              {task.category}
            </span>
          )}
          {task.due_date && (
            <span className="flex items-center gap-1 text-xs text-zinc-600">
              <CalendarIcon className="w-3.5 h-3.5" strokeWidth={1.5} />
              {format(parseISO(task.due_date), "MMM d, yyyy")}
            </span>
          )}
          {task.duration_minutes && (
            <span className="flex items-center gap-1 text-xs text-zinc-600">
              <Timer className="w-3.5 h-3.5" strokeWidth={1.5} />
              {task.duration_minutes} min
            </span>
          )}
        </div>

        {task.description && (
          <div className="border-l-2 border-zinc-300 pl-4 py-1 mb-8 text-sm text-zinc-600 whitespace-pre-line leading-relaxed">
            {task.description}
          </div>
        )}

        {subtasks.length > 0 ? (
          <div>
            <div className="flex items-center justify-between mb-3">
              <p className="text-xs font-semibold uppercase tracking-[0.15em] text-zinc-500">
                Subtasks
              </p>
              <span className="text-xs text-zinc-400 font-mono">
                {subDone}/{subtasks.length}
              </span>
            </div>
            <div className="h-1 bg-zinc-100 mb-5">
              <div
                className="h-full bg-[#002FA7] transition-all"
                style={{ width: `${progressPct}%` }}
              />
            </div>
            <ul className="border-t border-zinc-200">
              {subtasks.map((s) => (
                <li
                  key={s.id}
                  className="flex items-center gap-3 py-3 border-b border-zinc-200"
                  data-testid={`public-subtask-${s.id}`}
                >
                  <span
                    className={`w-4 h-4 border flex-shrink-0 flex items-center justify-center ${
                      s.completed
                        ? "border-[#002FA7] bg-[#002FA7] text-white"
                        : "border-zinc-400"
                    }`}
                  >
                    {s.completed && <Check className="w-3 h-3" strokeWidth={3} />}
                  </span>
                  <span
                    className={`text-sm ${s.completed ? "line-through text-zinc-400" : "text-zinc-800"}`}
                  >
                    {s.title}
                  </span>
                </li>
              ))}
            </ul>
          </div>
        ) : (
          <p className="text-sm text-zinc-400 italic">No subtasks yet.</p>
        )}

        <div className="mt-10 pt-6 border-t border-zinc-200">
          <a
            href={`/?utm_source=share&utm_medium=public_task&utm_campaign=viral_share`}
            className="inline-flex items-center gap-2 text-xs uppercase tracking-[0.12em] font-medium px-4 py-2.5 bg-zinc-900 hover:bg-[#002FA7] text-white transition-colors"
            data-testid="public-cta"
          >
            Try BreakItDown — break your own tasks down →
          </a>
        </div>
      </main>
      <PublicFooter />
      <PoweredByBadge />
    </div>
  );
}

function PoweredByBadge() {
  // Sticky bottom-right badge — turns every shared task into a discoverable
  // entry point for the app. UTM tags make share-link conversions attributable.
  return (
    <a
      href={`/?utm_source=share&utm_medium=badge&utm_campaign=viral_share`}
      target="_blank"
      rel="noopener noreferrer"
      className="fixed bottom-4 right-4 z-40 inline-flex items-center gap-2 bg-zinc-900 hover:bg-[#002FA7] text-white pl-2.5 pr-3 py-2 shadow-lg transition-colors group"
      data-testid="powered-by-badge"
      aria-label="Made with BreakItDown — try it free"
    >
      <span className="w-6 h-6 bg-white/15 group-hover:bg-white/25 flex items-center justify-center shrink-0">
        <Scissors className="w-3 h-3" strokeWidth={2} />
      </span>
      <span className="leading-tight">
        <span className="block text-[9px] uppercase tracking-[0.18em] text-white/70">Made with</span>
        <span className="block text-xs font-semibold tracking-tight">BreakItDown</span>
      </span>
      <ArrowRight className="w-3.5 h-3.5 ml-0.5 opacity-0 group-hover:opacity-100 -translate-x-1 group-hover:translate-x-0 transition-all" />
    </a>
  );
}

function Header() {
  return (
    <header className="border-b border-zinc-200 bg-white">
      <div className="max-w-3xl mx-auto px-6 md:px-10 py-4 flex items-center gap-3">
        <a href="/" className="flex items-center gap-3 group">
          <div className="w-9 h-9 bg-zinc-900 text-white flex items-center justify-center">
            <Scissors className="w-4 h-4" strokeWidth={1.75} />
          </div>
          <div>
            <div className="text-base font-semibold tracking-tight text-zinc-900 leading-none group-hover:text-[#002FA7]">
              BreakItDown
            </div>
            <div className="text-[10px] uppercase tracking-[0.18em] text-zinc-400 mt-1">
              Break it down. Get it done.
            </div>
          </div>
        </a>
      </div>
    </header>
  );
}

function PublicFooter() {
  return (
    <footer className="border-t border-zinc-200 py-6 text-center">
      <a
        href="https://charlottebryan.com.au/"
        target="_blank"
        rel="noopener noreferrer"
        className="text-xs uppercase tracking-[0.15em] text-zinc-500 hover:text-[#002FA7] transition-colors"
      >
        A <span className="font-semibold text-zinc-900">Charlotte Bryan</span> app ↗
      </a>
    </footer>
  );
}
